// packages/web-ui/src/state/utilityDrawer.ts
//
// W2-S9 — `utilityDrawer` zustand slice.
//
// Owns the drawer-side state that is NOT layout (the AppShell's W2-S2
// `navigation` slice keeps `utilityDrawerOpen` and `utilityDrawerWidth`).
//
// Scope of THIS slice:
//   - Active drawer tab (`activity` | `detail` | `debug`).
//   - Per-destination last-used drawer tab (used for the Settings-destination
//     fallback per UX §7.3 and for click-overrides surviving destination switch).
//   - Power-user override flag for the Debug tab (`Ctrl+Shift+J`). Session-only
//     — explicit RB-locked decision; does NOT persist across reload (UX §7.6).
//   - Settings flags read by the drawer (`showDebugTab`, `autoOpenOnError`).
//     Default `showDebugTab = false`, `autoOpenOnError = true`. Until W6-S1
//     wires `GET /api/v1/settings`, the drawer reads these from the slice
//     (callers can mutate them via `setSetting` for tests + the future
//     Settings panel).
//
// OUT OF SCOPE here (owned by other DEV stories per the parallel-lane brief):
//   - SSE event-log buffer (W2-S5 `sse` slice extension).
//   - Activity-tab filter chips, pause/clear/export wiring.
//   - Server-side `GET /api/v1/diagnostics/agent-log/export` route.
//   - localStorage persistence keyed by `(workspaceHash, destination)` —
//     placeholder accessor lives below; full impl ships with Task 9 of the
//     story doc.
//
// The slice is NOT yet composed into the root store. The W2-S2 store still
// only mounts `navigation`. Tests instantiate this slice via a vanilla
// store (matches the `changeset.ts` pattern from W4-S4).

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type { ActiveDestination } from './navigation.js';

/** The three drawer tabs (UX §7.3). */
export type DrawerTab = 'activity' | 'detail' | 'debug';

/** Map of last-used drawer-tab per destination — feeds UX §7.3 fallback. */
export type LastUsedByDestination = Partial<Record<ActiveDestination, DrawerTab>>;

export interface UtilityDrawerSliceState {
  /** Currently-active drawer tab. Derived from `getDefaultDrawerTab` on destination change. */
  activeTab: DrawerTab;
  /** Per-destination last-used tab map (RAM mirror of the localStorage key). */
  lastUsedByDestination: LastUsedByDestination;
  /**
   * Power-user override flag — flipped to `true` on `Ctrl+Shift+J`. Session-only
   * (page reload resets to `false`). When `true`, the Debug tab is visible in
   * the strip regardless of the `showDebugTab` setting (UX §7.6 RB-locked).
   */
  debugOverride: boolean;
  /**
   * `tessa-lean.ui.showDebugTab` — gates Debug-tab visibility. Default `false`.
   * Until W6-S1 wires `GET /api/v1/settings`, this is read from the slice and
   * the (future) Settings panel calls `setShowDebugTab` on toggle.
   */
  showDebugTab: boolean;
  /**
   * `tessa-lean.ui.utilityDrawer.autoOpenOnError` — when `true` (default), an
   * SSE `error` event with the drawer closed flips it open onto the Activity
   * tab. THIS slice owns the flag; the `attachSseToUtilityDrawer` bridge
   * (W2-S9 Task 3, parallel-lane scope) reads it before dispatching.
   */
  autoOpenOnError: boolean;
}

export interface UtilityDrawerSliceActions {
  /** Set the active drawer tab. Idempotent. */
  setActiveTab: (tab: DrawerTab) => void;
  /**
   * Manual click handler — sets the active tab AND records the click as the
   * last-used tab for the given destination, so the override survives a
   * destination round-trip per UX §7.3 (line 1044).
   */
  selectTabForDestination: (tab: DrawerTab, destination: ActiveDestination) => void;
  /** Flip the power-user Debug override flag. */
  setDebugOverride: (next: boolean) => void;
  /** Setter for `showDebugTab`. Used by the Settings panel (W6-S1). */
  setShowDebugTab: (next: boolean) => void;
  /** Setter for `autoOpenOnError`. */
  setAutoOpenOnError: (next: boolean) => void;
  /** Test/debug helper — reset slice state to defaults. */
  _reset: () => void;
}

export type UtilityDrawerSlice = UtilityDrawerSliceState & UtilityDrawerSliceActions;

export const UTILITY_DRAWER_INITIAL_STATE: UtilityDrawerSliceState = {
  activeTab: 'activity',
  lastUsedByDestination: {},
  debugOverride: false,
  showDebugTab: false,
  autoOpenOnError: true,
};

export const createUtilityDrawerSlice: StateCreator<
  UtilityDrawerSlice,
  [],
  [],
  UtilityDrawerSlice
> = (set) => ({
  ...UTILITY_DRAWER_INITIAL_STATE,

  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  selectTabForDestination: (tab, destination) => {
    set((state) => ({
      activeTab: tab,
      lastUsedByDestination: {
        ...state.lastUsedByDestination,
        [destination]: tab,
      },
    }));
  },

  setDebugOverride: (next) => {
    set({ debugOverride: next });
  },

  setShowDebugTab: (next) => {
    set({ showDebugTab: next });
  },

  setAutoOpenOnError: (next) => {
    set({ autoOpenOnError: next });
  },

  _reset: () => {
    set({ ...UTILITY_DRAWER_INITIAL_STATE });
  },
});

/** Vanilla-store factory for standalone use (tests, drawer-internal call-sites). */
export function createUtilityDrawerStore() {
  return createStore<UtilityDrawerSlice>()((...a) => createUtilityDrawerSlice(...a));
}
